import React, { useEffect, useState } from "react";
import {
    COMMAND,
    GENERIC_REQUEST,
    getApiPath,
    jsonToQueryString,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
    VIEW,
} from "../../api/ApiConst";
import ApiCaller from "../../api/ApiCaller";
import DynamicResponseView from "./DynamicResponseView";

const DynamicRequestForm = () => {
    const [formData, setFormData] = useState({});
    const [responseData, setResponsedata] = useState(null);
    const [requestDetail, setRequestDetail] = useState(null);
    const [request, setRequest] = useState({ data: [] });

    const url = new URL(window.location.href);
    const requestId = url.pathname.split("/")[2];

    // Fetch request parameters
    const getRequestParameters = () => {
        const requestParameter = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll(
            "{request-id}",
            requestId
        );
        const jsonRequest = {
            page: 0,
            size: 100,
            searchFilters: [],
            sortRules: [],
        };

        ApiCaller.callPostApi(
            requestParameter,
            jsonRequest,
            {},
            (response) => {
                setRequest(response);
                initializeFormData(response.data);
            },
            (error) => {
                console.error("Error fetching parameters:", error);
            }
        );
    };

    // Fetch details of the selected request
    const fetchRequestDetail = (requestId) => {
        const singleRequestUrl = getApiPath(GENERIC_REQUEST, VIEW, requestId);

        ApiCaller.callGetApi(
            singleRequestUrl,
            {},
            (response) => {
                setRequestDetail(response);
            },
            (error) => {
                console.error("Error fetching request detail:", error);
            }
        );
    };

    useEffect(() => {
        fetchRequestDetail(requestId);
        getRequestParameters();
    }, []);

    // Initialize form fields dynamically
    const initializeFormData = (fields) => {
        const initialData = {};
        fields.forEach((field) => {
            switch (field.type) {
                case "BOOLEAN":
                    initialData[field.name] = false;
                    break;
                case "DATE":
                case "DATETIME":
                    initialData[field.name] = ""; // Default value for date/datetime
                    break;
                default:
                    initialData[field.name] = "";
            }
        });
        setFormData(initialData);
    };

    // Helper function to format date and time to "YYYY-MM-DD HH:mm"

    // Convert ISO to datetime-local
    const toDatetimeLocalFormat = (isoDate) => {
        if (!isoDate) return "";
        const date = new Date(isoDate);
        const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000); // Adjust for timezone
        return offsetDate.toISOString().slice(0, 16); // Get `YYYY-MM-DDTHH:mm` format
    };

    // Convert datetime-local to ISO (Z format)
    const toISOFormat = (datetimeLocal) => {
        if (!datetimeLocal) return "";
        const date = new Date(datetimeLocal);
        return date.toISOString(); // Convert back to full ISO format with Z
    };

    // Handle input change
    const handleInputChange = (e, fieldType, fieldName) => {
        let value;

        if (fieldType === "BOOLEAN") {
            value = e.target.checked;
        } else if (fieldType === "DATETIME") {
            const rawValue = e.target.value; // Native browser datetime-local format (YYYY-MM-DDTHH:mm)
            value = toISOFormat(rawValue); // Convert to MongoDB ISO format
        } else if (fieldType === "DATE") {
            value = e.target.value; // Use as is for DATE
        } else {
            value = e.target.value;
        }

        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };



    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        const requestUrl = getApiPath(requestDetail.name, COMMAND);

        if (requestDetail.type === "GET") {
            const finalUrl =
                Object.keys(formData).length > 0
                    ? `${requestUrl}?${jsonToQueryString(formData)}`
                    : requestUrl;

            ApiCaller.callGetApi(
                finalUrl,
                {},
                (response) => {
                    setResponsedata(response);
                },
                (error) => {
                    console.error("Error on GET request:", error);
                }
            );
        } else {
            ApiCaller.callPostApi(
                requestUrl,
                formData,
                {},
                (response) => {
                    setResponsedata(response);
                },
                (error) => {
                    console.error("Error on POST request:", error);
                }
            );
        }
    };

    // Render dynamic input fields based on type
    const renderField = (field) => {
        const { name, type, description, required } = field;

        switch (type) {
            case "STRING":
                return (
                    <input
                        type="text"
                        id={name}
                        className="form-control"
                        placeholder={description || ""}
                        value={formData[name] || ""}
                        onChange={(e) => handleInputChange(e, type, name)}
                        required={required}
                    />
                );
            case "BOOLEAN":
                return (
                    <div className="form-check">
                        <input
                            type="checkbox"
                            id={name}
                            className="form-check-input"
                            checked={formData[name] || false}
                            onChange={(e) => handleInputChange(e, type, name)}
                        />
                        <label htmlFor={name} className="form-check-label">
                            {description || name}
                        </label>
                    </div>
                );
            case "NUMBER":
                return (
                    <input
                        type="number"
                        id={name}
                        className="form-control"
                        placeholder={description || ""}
                        value={formData[name] || ""}
                        onChange={(e) => handleInputChange(e, type, name)}
                        required={required}
                    />
                );
            case "DATE":
                return (
                    <input
                        type="date"
                        id={name}
                        className="form-control"
                        value={formData[name] || ""}
                        onChange={(e) => handleInputChange(e, type, name)}
                        required={required}
                    />
                );
            case "DATETIME":
                return (
                    <input
                        type="datetime-local"
                        id={name}
                        className="form-control"
                        value={
                            formData[name]
                                ? toDatetimeLocalFormat(formData[name]) // Convert ISO to `datetime-local` format
                                : ""
                        }
                        onChange={(e) => handleInputChange(e, type, name)}
                        required={required}
                    />
                );

            default:
                return (
                    <input
                        type="text"
                        id={name}
                        className="form-control"
                        placeholder={description || ""}
                        value={formData[name] || ""}
                        onChange={(e) => handleInputChange(e, type, name)}
                        required={required}
                    />
                );
        }
    };

    return (
        <div className="container mt-4">
            <h3>Request Form</h3>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    {request.data.map((field) => (
                        <div className="col-md-4 mb-3" key={field.id}>
                            <label htmlFor={field.name} className="form-label">
                                {field.name} {field.required ? "*" : ""}
                            </label>
                            {renderField(field)}
                        </div>
                    ))}
                </div>
                <button type="submit" className="btn btn-primary">
                    Submit
                </button>
            </form>

            <div className="mt-4">
                <DynamicResponseView
                    response={responseData ?? {}}
                    requestMeta={requestDetail}

                />
            </div>
        </div>
    );
};

export default DynamicRequestForm;
